import { useAppStore } from '@h5-journey/stores'
import axios from 'axios'

const axiosInstance = axios.create({
  timeout: 30 * 1000,
  headers: {
    'Content-Type': 'application/json'
  }
})

const DEFAULT_REQUEST_LOCALE = 'zh-CN'
const SUPPORTED_REQUEST_LOCALES = ['zh-CN', 'zh-HK', 'en'] as const

type RequestLocale = (typeof SUPPORTED_REQUEST_LOCALES)[number]

/** 缓存 Request-From，避免每次请求都读取 sessionStorage */
let cachedRequestFrom: string | null = null

/**
 * 设置公共请求头 Request-From（全局设置一次即可）。
 * 可在应用初始化时主动调用以提前缓存。
 */
export function setRequestFrom(value?: string) {
  cachedRequestFrom = value ?? (typeof window !== 'undefined' ? sessionStorage.getItem('Request-From') || '' : '')
}

function normalizeRequestLocale(locale?: string | null): RequestLocale {
  if (!locale) return DEFAULT_REQUEST_LOCALE

  const normalized = locale.replace('_', '-').toLowerCase()

  if (normalized === 'zh-cn' || normalized.startsWith('zh-hans')) return 'zh-CN'
  if (normalized === 'zh-hk' || normalized === 'zh-tw' || normalized.startsWith('zh-hant')) return 'zh-HK'
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en'

  return SUPPORTED_REQUEST_LOCALES.includes(locale as RequestLocale)
    ? (locale as RequestLocale)
    : DEFAULT_REQUEST_LOCALE
}

function getRequestLocale(): RequestLocale {
  if (typeof window === 'undefined') return DEFAULT_REQUEST_LOCALE

  try {
    return normalizeRequestLocale(document.documentElement.lang || localStorage.getItem('locale'))
  } catch {
    return DEFAULT_REQUEST_LOCALE
  }
}

/**
 * 判断RequestFrom格式是否有效，避免常见的类型、空字符串被错误的存到sessionStorage导致异常
 * @param requestFrom 
 * @returns 是否有效
 */
function isRequestFromValid(requestFrom: unknown): boolean {
  return typeof requestFrom === 'string' && requestFrom !== '' && requestFrom !== 'null' && requestFrom !== 'undefined' && requestFrom !== 'object'
}

axiosInstance.interceptors.request.use((config) => {
  // 首次请求时若未主动调用 setRequestFrom，则自动从 sessionStorage 读取并缓存
  if (!isRequestFromValid(cachedRequestFrom)) {
    cachedRequestFrom = typeof window !== 'undefined' ? sessionStorage.getItem('Request-From') || '' : ''
  }
  config.headers = config.headers || {}
  if (isRequestFromValid(cachedRequestFrom)) config.headers['Request-From'] = cachedRequestFrom
  else delete config.headers['Request-From']

  const locale = getRequestLocale()
  config.headers['Accept-Language'] = config.headers['Accept-Language'] ?? locale
  config.headers['locale'] = config.headers['locale'] ?? locale
  return config
})

axiosInstance.interceptors.response.use((v) => {
  // 获取登录的token
  if (v.headers['authorization']) {
    const appStore = useAppStore()
    appStore.setItem({ SET_TOKEN: v.headers['authorization'] })
  }
  return v
})

export default axiosInstance
